import { useState, useEffect, useCallback } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { contentTypeForFile } from '../../shared/utils/contentTypeForFile';
import { API_URL } from '../../config/env';

const BASE_PATH = `${API_URL}/v1/admin/organization/documents`;

export default function useDocumentLibrary() {
  const isMounted = useIsMounted();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(BASE_PATH);
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to load documents');
      setItems([]);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    const contentType = contentTypeForFile(file);
    if (!contentType) {
      const err = new Error('Unsupported file type');
      setError(err.message);
      throw err;
    }

    setUploading(true);
    setError(null);
    let pendingId = null;
    try {
      const prepareRes = await fetchWithAuth(BASE_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          media: {
            filename: file.name,
            content_type: contentType,
            byte_size: file.size,
          },
        }),
      });
      if (!prepareRes.ok) {
        const body = await prepareRes.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || prepareRes.statusText);
      }
      const { media, upload } = await prepareRes.json();
      pendingId = media.id;

      const putHeaders = { ...(upload.headers || {}), 'Content-Type': contentType };

      const putRes = await fetch(upload.url, {
        method: upload.method || 'PUT',
        body: file,
        headers: putHeaders,
      });
      if (!putRes.ok) {
        throw new Error('Upload to storage failed');
      }

      const completeRes = await fetchWithAuth(`${BASE_PATH}/${media.id}`, { method: 'PATCH' });
      if (!completeRes.ok) {
        const body = await completeRes.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || completeRes.statusText);
      }

      await fetchDocuments();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      if (pendingId) {
        try {
          await fetchWithAuth(`${BASE_PATH}/${pendingId}`, { method: 'DELETE' });
        } catch (cleanupErr) {
          if (!isMounted.current) return;
          console.error('Failed to remove incomplete upload record:', cleanupErr);
        }
      }
      await fetchDocuments();
      setError(err.message || 'Upload failed');
      throw err;
    } finally {
      if (!isMounted.current) return;
      setUploading(false);
    }
  }, [fetchDocuments, isMounted]);

  const deleteItem = useCallback(async (id) => {
    if (!id) return;
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE_PATH}/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || res.statusText);
      }
      await fetchDocuments();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Delete failed');
      throw err;
    }
  }, [fetchDocuments, isMounted]);

  const setFieldEngineerVisible = useCallback(async (id, visible) => {
    if (!id) return;
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE_PATH}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ media: { visible_to_field_engineers: visible } }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || res.statusText);
      }
      await fetchDocuments();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to update visibility');
      throw err;
    }
  }, [fetchDocuments, isMounted]);

  const setTags = useCallback(async (id, tagIds) => {
    if (!id) return;
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE_PATH}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ media: { tag_ids: tagIds } }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || res.statusText);
      }
      await fetchDocuments();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to update tags');
      throw err;
    }
  }, [fetchDocuments, isMounted]);

  const downloadItem = useCallback(async (id) => {
    if (!id) return;
    const res = await fetchWithAuth(`${BASE_PATH}/${id}/download`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.errors?.join(', ') || body.error || res.statusText);
    }
    const { url, filename } = await res.json();
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  // `ocr` is optional: omitted, the document keeps whatever choice it already had, so a plain
  // retry never silently turns OCR off for a scanned document that needs it.
  const retryKnowledgeIndex = useCallback(async (id, { ocr } = {}) => {
    if (!id) return;
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE_PATH}/${id}/retry_knowledge_index`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ocr === undefined ? {} : { ocr }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || res.statusText);
      }
      await fetchDocuments();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to retry indexing');
      throw err;
    }
  }, [fetchDocuments, isMounted]);

  return {
    items,
    loading,
    error,
    uploading,
    refresh: fetchDocuments,
    uploadFile,
    deleteItem,
    downloadItem,
    setFieldEngineerVisible,
    setTags,
    retryKnowledgeIndex,
  };
}
